import React from 'react';
import superjson from 'superjson';
import { Link } from 'react-router-dom';
import styles from './repoforge.module.css';
import { useAuth } from '../helpers/useAuth';
import { OnboardingTutorial, useShowSectionOnboarding } from '../components/OnboardingTutorial';

type RepoVisibility = 'public' | 'private' | 'internal';
type MemberRole = 'viewer' | 'collaborator' | 'editor' | 'developer' | 'admin' | 'owner';
type PermissionAction = 'read' | 'write' | 'delete' | 'review' | 'merge' | 'manage';
type RuleEffect = 'allow' | 'deny';

type Repo = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  visibility: RepoVisibility;
  gitUrl: string;
  ownerDisplayName?: string;
};

type Participant = {
  id: string;
  type: 'human' | 'agent';
  handle: string;
  displayName: string;
};

type RepoPermissionResponse = {
  role: string | null;
  effective: Record<string, boolean>;
  rules: Array<{
    id: string;
    participant_id: string | null;
    role: string | null;
    effect: RuleEffect;
    action: PermissionAction;
    branch_pattern: string;
    file_path_pattern: string;
  }>;
};

type FormState = {
  repoName: string;
  repoSlug: string;
  repoDescription: string;
  repoVisibility: RepoVisibility;
  participantHandle: string;
  participantDisplayName: string;
  participantType: 'agent' | 'human';
  memberParticipantId: string;
  memberRole: MemberRole;
  aclParticipantId: string;
  aclRole: '' | MemberRole;
  aclEffect: RuleEffect;
  aclAction: PermissionAction;
  aclBranchPattern: string;
  aclFilePattern: string;
  inspectParticipantId: string;
  inspectBranch: string;
  inspectPath: string;
};

const DEFAULT_FORM: FormState = {
  repoName: '',
  repoSlug: '',
  repoDescription: '',
  repoVisibility: 'private',
  participantHandle: '',
  participantDisplayName: '',
  participantType: 'agent',
  memberParticipantId: '',
  memberRole: 'developer',
  aclParticipantId: '',
  aclRole: '',
  aclEffect: 'allow',
  aclAction: 'read',
  aclBranchPattern: '*',
  aclFilePattern: '*',
  inspectParticipantId: '',
  inspectBranch: '',
  inspectPath: '',
};

async function parseResponse<T>(res: Response): Promise<T> {
  const raw = await res.text();
  if (!raw.trim()) return {} as T;
  try {
    return superjson.parse(raw) as T;
  } catch {
    try {
      return JSON.parse(raw) as T;
    } catch {
      const compact = raw.replace(/\s+/g, ' ').trim();
      const preview = compact.slice(0, 140);
      const isHtml = /^<!doctype html>|^<html[\s>]/i.test(compact);
      const contentType = res.headers.get('content-type') || 'unknown';
      const status = `${res.status} ${res.statusText}`.trim();
      const message = isHtml
        ? `RepoForge API returned HTML instead of JSON (${status}, ${contentType}). This usually means you were redirected to a page route or auth page.`
        : `RepoForge API returned invalid JSON (${status}, ${contentType}).`;
      throw new Error(`${message} Response preview: ${preview}`);
    }
  }
}

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export default function RepoForgePage() {
  const { authState } = useAuth();
  const currentUserId = authState.type === 'authenticated' ? authState.user?.id : null;
  const showTutorial = useShowSectionOnboarding('repoforge', currentUserId);
  const [tutorialDismissed, setTutorialDismissed] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [repos, setRepos] = React.useState<Repo[]>([]);
  const [participants, setParticipants] = React.useState<Participant[]>([]);
  const [selectedRepoId, setSelectedRepoId] = React.useState('');
  const [form, setForm] = React.useState<FormState>(DEFAULT_FORM);
  const [memberRoles, setMemberRoles] = React.useState<Record<string, string | null>>({});
  const [permissionView, setPermissionView] = React.useState<RepoPermissionResponse | null>(null);

  const selectedRepo = React.useMemo(
    () => repos.find((repo) => repo.id === selectedRepoId) || null,
    [repos, selectedRepoId],
  );

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [repoRes, participantRes] = await Promise.all([
        fetch('/_api/repo-forge/repos', { credentials: 'include' }),
        fetch('/_api/repo-forge/participants', { credentials: 'include' }),
      ]);
      const repoData = await parseResponse<{ repos: Repo[]; error?: string }>(repoRes);
      const participantData = await parseResponse<{ participants: Participant[]; error?: string }>(
        participantRes,
      );
      if (!repoRes.ok) throw new Error(repoData.error || 'Failed to load repositories');
      if (!participantRes.ok)
        throw new Error(participantData.error || 'Failed to load participants');

      const reposList = repoData.repos || [];
      const participantsList = participantData.participants || [];
      setRepos(reposList);
      setParticipants(participantsList);

      const nextRepoId = selectedRepoId || reposList[0]?.id || '';
      setSelectedRepoId(nextRepoId);

      const firstParticipantId = participantsList[0]?.id || '';
      setForm((prev) => ({
        ...prev,
        memberParticipantId: prev.memberParticipantId || firstParticipantId,
        aclParticipantId: prev.aclParticipantId || firstParticipantId,
        inspectParticipantId: prev.inspectParticipantId || firstParticipantId,
      }));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed loading RepoForge'));
    } finally {
      setLoading(false);
    }
  }, [selectedRepoId]);

  const refreshMemberRoles = React.useCallback(async () => {
    if (!selectedRepoId || participants.length === 0) {
      setMemberRoles({});
      return;
    }
    const roleMap: Record<string, string | null> = {};
    await Promise.all(
      participants.map(async (participant) => {
        const res = await fetch(
          `/_api/repo-forge/permissions?repoId=${encodeURIComponent(selectedRepoId)}&participantId=${encodeURIComponent(participant.id)}`,
          { credentials: 'include' },
        );
        const data = await parseResponse<{ role: string | null }>(res);
        roleMap[participant.id] = data.role || null;
      }),
    );
    setMemberRoles(roleMap);
  }, [participants, selectedRepoId]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => {
    void refreshMemberRoles();
  }, [refreshMemberRoles]);

  React.useEffect(() => {
    if (!selectedRepoId || !form.inspectParticipantId) {
      setPermissionView(null);
      return;
    }
    const params = new URLSearchParams({
      repoId: selectedRepoId,
      participantId: form.inspectParticipantId,
    });
    void (async () => {
      const res = await fetch(`/_api/repo-forge/permissions?${params.toString()}`, {
        credentials: 'include',
      });
      const data = await parseResponse<RepoPermissionResponse & { error?: string }>(res);
      if (res.ok) setPermissionView(data);
    })();
  }, [form.inspectParticipantId, selectedRepoId]);

  const runAction = async (fn: () => Promise<void>, successMessage: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(successMessage);
      await refresh();
      if (selectedRepoId) {
        await refreshMemberRoles();
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Action failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateRepo = async (event: React.FormEvent) => {
    event.preventDefault();
    await runAction(async () => {
      const res = await fetch('/_api/repo-forge/repos', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          name: form.repoName,
          slug: form.repoSlug || undefined,
          description: form.repoDescription || undefined,
          visibility: form.repoVisibility,
        }),
      });
      const data = await parseResponse<{ repo?: Repo; error?: string }>(res);
      if (!res.ok) throw new Error(data.error || 'Failed to create repository');
      if (data.repo?.id) setSelectedRepoId(data.repo.id);
      setForm((prev) => ({ ...prev, repoName: '', repoSlug: '', repoDescription: '' }));
    }, 'Repository created');
  };

  const handleCreateParticipant = async (event: React.FormEvent) => {
    event.preventDefault();
    await runAction(async () => {
      const res = await fetch('/_api/repo-forge/participants', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          type: form.participantType,
          handle: form.participantHandle,
          displayName: form.participantDisplayName,
        }),
      });
      const data = await parseResponse<{ error?: string }>(res);
      if (!res.ok) throw new Error(data.error || 'Failed to create participant');
      setForm((prev) => ({ ...prev, participantHandle: '', participantDisplayName: '' }));
    }, 'Participant created');
  };

  const handleSetMember = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRepoId) return;
    await runAction(async () => {
      const res = await fetch('/_api/repo-forge/permissions', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          mode: 'set_member',
          repoId: selectedRepoId,
          participantId: form.memberParticipantId,
          role: form.memberRole,
        }),
      });
      const data = await parseResponse<{ error?: string }>(res);
      if (!res.ok) throw new Error(data.error || 'Failed to save member role');
    }, 'Member role updated');
  };

  const handleRemoveMember = async (participantId: string) => {
    if (!selectedRepoId) return;
    await runAction(async () => {
      const res = await fetch('/_api/repo-forge/permissions', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          mode: 'remove_member',
          repoId: selectedRepoId,
          participantId,
        }),
      });
      const data = await parseResponse<{ error?: string }>(res);
      if (!res.ok) throw new Error(data.error || 'Failed to remove member');
    }, 'Member removed');
  };

  const handleAddRule = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRepoId) return;
    await runAction(async () => {
      const res = await fetch('/_api/repo-forge/permissions', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          mode: 'add_rule',
          repoId: selectedRepoId,
          participantId: form.aclParticipantId || undefined,
          role: form.aclRole || undefined,
          effect: form.aclEffect,
          action: form.aclAction,
          branchPattern: form.aclBranchPattern || '*',
          filePathPattern: form.aclFilePattern || '*',
        }),
      });
      const data = await parseResponse<{ error?: string }>(res);
      if (!res.ok) throw new Error(data.error || 'Failed to add ACL rule');
    }, 'ACL rule added');
  };

  const handleRemoveRule = async (ruleId: string) => {
    if (!selectedRepoId) return;
    await runAction(async () => {
      const res = await fetch('/_api/repo-forge/permissions', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          mode: 'remove_rule',
          repoId: selectedRepoId,
          ruleId,
        }),
      });
      const data = await parseResponse<{ error?: string }>(res);
      if (!res.ok) throw new Error(data.error || 'Failed to remove ACL rule');
    }, 'ACL rule removed');
  };

  const handleInspectPermissions = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRepoId) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const params = new URLSearchParams({
        repoId: selectedRepoId,
        participantId: form.inspectParticipantId,
      });
      if (form.inspectBranch.trim()) params.set('branchName', form.inspectBranch.trim());
      if (form.inspectPath.trim()) params.set('filePath', form.inspectPath.trim());
      const res = await fetch(`/_api/repo-forge/permissions?${params.toString()}`, {
        credentials: 'include',
      });
      const data = await parseResponse<RepoPermissionResponse & { error?: string }>(res);
      if (!res.ok) throw new Error(data.error || 'Failed to inspect permissions');
      setPermissionView(data);
      setNotice('Permission check loaded');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Permission check failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.page}>
      {showTutorial && !tutorialDismissed && (
        <OnboardingTutorial
          type="repoforge"
          userId={currentUserId}
          onComplete={() => setTutorialDismissed(true)}
        />
      )}
      <section className={styles.card} aria-busy={busy}>
        <header className={styles.header}>
          <span className={styles.badge}>
            <img
              src="/icons/repoforge-icon.png"
              alt=""
              aria-hidden="true"
              className={styles.badgeIcon}
            />
            RepoForge
          </span>
          <h1 className={styles.title}>Repositories, Members, and Access Control</h1>
          <p className={styles.subtitle}>
            Create repositories, set private/public visibility, add collaborators, and lock access
            down to branch and file/folder patterns.
          </p>
          <div className={styles.headerActions}>
            <button type="button" className={styles.secondaryBtn} onClick={() => void refresh()}>
              Refresh
            </button>
            <Link to="/pricing#repoforge" className={styles.linkBtn}>
              Manage Add-on
            </Link>
          </div>
        </header>

        {error ? <p className={styles.error}>{error}</p> : null}
        {notice ? <p className={styles.notice}>{notice}</p> : null}
        {loading ? <p className={styles.muted}>Loading RepoForge...</p> : null}

        <div className={styles.layout}>
          <aside className={styles.repoList}>
            <h2>Repositories</h2>
            <div className={styles.repoItems}>
              {repos.length === 0 ? <p className={styles.muted}>No repos yet.</p> : null}
              {repos.map((repo) => (
                <button
                  key={repo.id}
                  type="button"
                  className={`${styles.repoItem} ${selectedRepoId === repo.id ? styles.repoItemActive : ''}`}
                  onClick={() => setSelectedRepoId(repo.id)}
                >
                  <strong>{repo.name}</strong>
                  <span>@{repo.slug}</span>
                  <span>{repo.visibility}</span>
                </button>
              ))}
            </div>
          </aside>

          <main className={styles.workspace}>
            <section className={styles.panel}>
              <h3>Create Repository</h3>
              <form className={styles.form} onSubmit={handleCreateRepo}>
                <label>
                  Name
                  <input
                    required
                    value={form.repoName}
                    onChange={(event) => updateField('repoName', event.target.value)}
                    placeholder="Tasking Mobile App"
                  />
                </label>
                <label>
                  Slug (optional)
                  <input
                    value={form.repoSlug}
                    onChange={(event) => updateField('repoSlug', event.target.value)}
                    placeholder="tasking-mobile-app"
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={form.repoDescription}
                    onChange={(event) => updateField('repoDescription', event.target.value)}
                    placeholder="Main product repo..."
                    rows={3}
                  />
                </label>
                <label>
                  Visibility
                  <select
                    value={form.repoVisibility}
                    onChange={(event) =>
                      updateField('repoVisibility', event.target.value as RepoVisibility)
                    }
                  >
                    <option value="private">Private</option>
                    <option value="internal">Internal</option>
                    <option value="public">Public</option>
                  </select>
                </label>
                <button type="submit" className={styles.primaryBtn} disabled={busy}>
                  Create Repo
                </button>
              </form>
            </section>

            <section className={styles.panel}>
              <h3>Create Participant (Human/Agent)</h3>
              <form className={styles.form} onSubmit={handleCreateParticipant}>
                <label>
                  Type
                  <select
                    value={form.participantType}
                    onChange={(event) =>
                      updateField('participantType', event.target.value as 'agent' | 'human')
                    }
                  >
                    <option value="agent">Agent</option>
                    <option value="human">Human</option>
                  </select>
                </label>
                <label>
                  Handle
                  <input
                    required
                    value={form.participantHandle}
                    onChange={(event) => updateField('participantHandle', event.target.value)}
                    placeholder="taskingbot-reviewer"
                  />
                </label>
                <label>
                  Display Name
                  <input
                    required
                    value={form.participantDisplayName}
                    onChange={(event) => updateField('participantDisplayName', event.target.value)}
                    placeholder="TaskingBot Reviewer"
                  />
                </label>
                <button type="submit" className={styles.primaryBtn} disabled={busy}>
                  Create Participant
                </button>
              </form>
            </section>

            <section className={styles.panel}>
              <h3>Selected Repo</h3>
              {selectedRepo ? (
                <div className={styles.repoSummary}>
                  <p>
                    <strong>{selectedRepo.name}</strong> ({selectedRepo.visibility})
                  </p>
                  <p>@{selectedRepo.slug}</p>
                  <p>{selectedRepo.gitUrl}</p>
                  <p>{selectedRepo.description || 'No description'}</p>
                </div>
              ) : (
                <p className={styles.muted}>Select or create a repo to manage access.</p>
              )}
            </section>

            <section className={styles.panel}>
              <h3>Members</h3>
              <form className={styles.inlineForm} onSubmit={handleSetMember}>
                <select
                  value={form.memberParticipantId}
                  onChange={(event) => updateField('memberParticipantId', event.target.value)}
                  disabled={!selectedRepoId}
                >
                  {participants.map((participant) => (
                    <option key={participant.id} value={participant.id}>
                      {participant.displayName} ({participant.type})
                    </option>
                  ))}
                </select>
                <select
                  value={form.memberRole}
                  onChange={(event) => updateField('memberRole', event.target.value as MemberRole)}
                  disabled={!selectedRepoId}
                >
                  <option value="viewer">viewer</option>
                  <option value="collaborator">collaborator</option>
                  <option value="editor">editor</option>
                  <option value="developer">developer</option>
                  <option value="admin">admin</option>
                  <option value="owner">owner</option>
                </select>
                <button
                  type="submit"
                  className={styles.primaryBtn}
                  disabled={!selectedRepoId || busy}
                >
                  Save Role
                </button>
              </form>

              <div className={styles.memberList}>
                {participants
                  .filter((participant) => Boolean(memberRoles[participant.id]))
                  .map((participant) => (
                    <div key={participant.id} className={styles.memberItem}>
                      <div>
                        <strong>{participant.displayName}</strong>
                        <span>{memberRoles[participant.id]}</span>
                      </div>
                      {memberRoles[participant.id] !== 'owner' ? (
                        <button
                          type="button"
                          className={styles.dangerBtn}
                          onClick={() => void handleRemoveMember(participant.id)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  ))}
              </div>
            </section>

            <section className={styles.panel}>
              <h3>File/Folder ACL Rules</h3>
              <form className={styles.form} onSubmit={handleAddRule}>
                <div className={styles.twoCol}>
                  <label>
                    Participant (optional)
                    <select
                      value={form.aclParticipantId}
                      onChange={(event) => updateField('aclParticipantId', event.target.value)}
                      disabled={!selectedRepoId}
                    >
                      <option value="">None</option>
                      {participants.map((participant) => (
                        <option key={participant.id} value={participant.id}>
                          {participant.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Role (optional)
                    <select
                      value={form.aclRole}
                      onChange={(event) =>
                        updateField('aclRole', event.target.value as '' | MemberRole)
                      }
                      disabled={!selectedRepoId}
                    >
                      <option value="">None</option>
                      <option value="viewer">viewer</option>
                      <option value="collaborator">collaborator</option>
                      <option value="editor">editor</option>
                      <option value="developer">developer</option>
                      <option value="admin">admin</option>
                      <option value="owner">owner</option>
                    </select>
                  </label>
                </div>
                <div className={styles.twoCol}>
                  <label>
                    Effect
                    <select
                      value={form.aclEffect}
                      onChange={(event) =>
                        updateField('aclEffect', event.target.value as RuleEffect)
                      }
                      disabled={!selectedRepoId}
                    >
                      <option value="allow">allow</option>
                      <option value="deny">deny</option>
                    </select>
                  </label>
                  <label>
                    Action
                    <select
                      value={form.aclAction}
                      onChange={(event) =>
                        updateField('aclAction', event.target.value as PermissionAction)
                      }
                      disabled={!selectedRepoId}
                    >
                      <option value="read">read</option>
                      <option value="write">write</option>
                      <option value="delete">delete</option>
                      <option value="review">review</option>
                      <option value="merge">merge</option>
                      <option value="manage">manage</option>
                    </select>
                  </label>
                </div>
                <label>
                  Branch Pattern
                  <input
                    value={form.aclBranchPattern}
                    onChange={(event) => updateField('aclBranchPattern', event.target.value)}
                    placeholder="main or release/*"
                  />
                </label>
                <label>
                  File/Folder Pattern
                  <input
                    value={form.aclFilePattern}
                    onChange={(event) => updateField('aclFilePattern', event.target.value)}
                    placeholder="src/payments/* or docs/*.md"
                  />
                </label>
                <button
                  type="submit"
                  className={styles.primaryBtn}
                  disabled={!selectedRepoId || busy}
                >
                  Add Rule
                </button>
              </form>

              <div className={styles.ruleList}>
                {(permissionView?.rules || []).map((rule) => (
                  <div key={rule.id} className={styles.ruleItem}>
                    <div>
                      <strong>{rule.effect.toUpperCase()}</strong> {rule.action} on{' '}
                      <code>{rule.file_path_pattern}</code> ({rule.branch_pattern})
                    </div>
                    <button
                      type="button"
                      className={styles.dangerBtn}
                      onClick={() => void handleRemoveRule(rule.id)}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className={styles.panel}>
              <h3>Permission Inspector</h3>
              <form className={styles.form} onSubmit={handleInspectPermissions}>
                <label>
                  Participant
                  <select
                    value={form.inspectParticipantId}
                    onChange={(event) => updateField('inspectParticipantId', event.target.value)}
                    disabled={!selectedRepoId}
                  >
                    {participants.map((participant) => (
                      <option key={participant.id} value={participant.id}>
                        {participant.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Branch (optional)
                  <input
                    value={form.inspectBranch}
                    onChange={(event) => updateField('inspectBranch', event.target.value)}
                    placeholder="main"
                  />
                </label>
                <label>
                  File/Folder path (optional)
                  <input
                    value={form.inspectPath}
                    onChange={(event) => updateField('inspectPath', event.target.value)}
                    placeholder="src/components/Button.tsx"
                  />
                </label>
                <button
                  type="submit"
                  className={styles.primaryBtn}
                  disabled={!selectedRepoId || busy}
                >
                  Check Access
                </button>
              </form>

              {permissionView ? (
                <div className={styles.permissionsGrid}>
                  {Object.entries(permissionView.effective || {}).map(([action, allowed]) => (
                    <div key={action} className={styles.permissionChip}>
                      <span>{action}</span>
                      <strong>{allowed ? 'allowed' : 'blocked'}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.muted}>Run a check to see effective permissions.</p>
              )}
            </section>
          </main>
        </div>
      </section>
    </div>
  );
}
